<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  Home,
  Users,
  BookOpen,
  CalendarDays,
  MessageSquare,
  ChevronDown,
  Settings,
} from 'lucide-vue-next'
import { useAuthStore } from '@/store/AuthStore'
import { useAppShellStore } from '@/store/AppShellStore'
import { paletteForKey, paletteFromColour, type PaletteColour } from '@/design-system/components'
import AccountMenu from './AccountMenu.vue'
import logoUrl from '@/assets/logo-glyph.svg'

/**
 * AppSidebar (§02) — the persistent 248px desktop rail. Brand, primary nav, a
 * collapsible "My classes" switcher (all classes, sorted by shared classSort),
 * and an account row pinned to the foot. The whole rail scrolls as one unit;
 * the class list has no inner scroll container. Themed via [data-sidebar-theme]
 * (§01 --sidebar-* tokens); only the rail recolours.
 */
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const appShell = useAppShellStore()

type NavKey = 'today' | 'classes' | 'planning' | 'schedule' | 'connect'
interface NavItem {
  key: NavKey
  label: string
  to: string
  icon: typeof Home
  teacherOnly: boolean
}

const NAV: NavItem[] = [
  { key: 'today', label: 'Today', to: '/today', icon: Home, teacherOnly: true },
  { key: 'classes', label: 'Classes', to: '/classes', icon: Users, teacherOnly: true },
  { key: 'planning', label: 'Planning', to: '/planning', icon: BookOpen, teacherOnly: true },
  { key: 'schedule', label: 'Schedule', to: '/schedule', icon: CalendarDays, teacherOnly: false },
  { key: 'connect', label: 'Connect', to: '/connect', icon: MessageSquare, teacherOnly: false },
]

const navItems = computed(() =>
  auth.isStudent ? NAV.filter((n) => !n.teacherOnly) : NAV,
)

function isNavActive(key: NavKey): boolean {
  const p = route.path
  switch (key) {
    case 'today':
      return p === '/today' || p === '/home'
    case 'classes':
      return p.startsWith('/classes') // overview AND any class workspace
    case 'planning':
      return p.startsWith('/planning')
    case 'schedule':
      return p.startsWith('/schedule')
    case 'connect':
      return p.startsWith('/connect')
  }
}

const activeClassId = computed(() =>
  route.path.startsWith('/classes/') ? (route.params.id as string | undefined) : undefined,
)

const classes = computed(() => appShell.sortedClasses)

function hueFor(cls: { id?: string; colour?: string | null }) {
  if (cls.colour) return paletteFromColour(cls.colour as PaletteColour)
  return paletteForKey(cls.id ?? '')
}

function openClass(id?: string) {
  if (id) router.push(`/classes/${id}`)
}

// Account row
const accountHue = computed(() =>
  paletteForKey(auth.currentUser?.id ?? auth.userName ?? 'teacher'),
)
const initials = computed(() => {
  const name = (auth.userName || '').trim()
  if (!name) return 'U'
  const parts = name.split(/\s+/)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase() || 'U'
})
const roleLabel = computed(() => (auth.isStudent ? 'Student' : 'Teacher'))

const accountMenuOpen = ref(false)
const accountRef = ref<HTMLElement | null>(null)
function onClickOutside(e: MouseEvent) {
  if (accountRef.value && !accountRef.value.contains(e.target as Node)) {
    accountMenuOpen.value = false
  }
}

onMounted(() => {
  appShell.loadClasses()
  document.addEventListener('click', onClickOutside)
})
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside))
</script>

<template>
  <aside class="rail" :data-sidebar-theme="appShell.sidebarTheme">
    <div class="rail__inner">
      <!-- Brand -->
      <RouterLink to="/today" class="rail__brand">
        <span
          class="rail__logo"
          :style="{ maskImage: `url(${logoUrl})`, WebkitMaskImage: `url(${logoUrl})` }"
        />
        <span class="rail__wordmark">Smart Teacher</span>
      </RouterLink>

      <!-- Primary nav -->
      <nav class="rail__nav">
        <RouterLink
          v-for="item in navItems"
          :key="item.key"
          :to="item.to"
          class="navitem"
          :class="{ 'navitem--active': isNavActive(item.key) }"
        >
          <span v-if="isNavActive(item.key)" class="navitem__bar" />
          <component :is="item.icon" :size="20" class="navitem__icon" />
          <span class="navitem__label">{{ item.label }}</span>
        </RouterLink>
      </nav>

      <!-- My classes -->
      <div v-if="!auth.isStudent" class="myclasses">
        <button type="button" class="myclasses__head" @click="appShell.toggleMyClasses()">
          <ChevronDown
            :size="13"
            class="myclasses__chev"
            :class="{ 'myclasses__chev--collapsed': !appShell.myClassesOpen }"
          />
          <span class="myclasses__eyebrow">My classes</span>
          <span class="myclasses__count">{{ classes.length }}</span>
        </button>
        <div v-show="appShell.myClassesOpen" class="myclasses__list">
          <button
            v-for="cls in classes"
            :key="cls.id"
            type="button"
            class="classrow"
            :class="{ 'classrow--active': activeClassId === cls.id }"
            @click="openClass(cls.id)"
          >
            <span class="classrow__dot" :style="{ background: hueFor(cls).solid }" />
            <span class="classrow__name">{{ cls.name }}</span>
            <span class="classrow__count">{{ cls.studentCount ?? 0 }}</span>
          </button>
        </div>
      </div>

      <!-- Spacer pins account to the foot when the list is short -->
      <div class="rail__spacer" />

      <!-- Account -->
      <div ref="accountRef" class="account">
        <div class="account__row">
          <span class="account__avatar" :style="{ background: accountHue.soft, color: accountHue.ink }">
            {{ initials }}
          </span>
          <div class="account__id">
            <div class="account__name">{{ auth.userName }}</div>
            <div class="account__meta">{{ roleLabel }}</div>
          </div>
          <button
            type="button"
            class="account__gear"
            aria-label="Account menu"
            @click.stop="accountMenuOpen = !accountMenuOpen"
          >
            <Settings :size="18" />
          </button>
        </div>
        <div v-if="accountMenuOpen" class="account__menu">
          <AccountMenu placement="up" @close="accountMenuOpen = false" />
        </div>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.rail {
  width: 248px;
  flex-shrink: 0;
  height: 100%;
  background: var(--sidebar-bg);
  border-right: 1px solid var(--sidebar-border);
  overflow-y: auto;
  font-family: var(--font-sans);
}
.rail__inner {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  box-sizing: border-box;
  padding: 18px 16px 14px;
}

/* Brand */
.rail__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 4px 18px;
  text-decoration: none;
}
.rail__logo {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  background: var(--sidebar-brand-logo);
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}
.rail__wordmark {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: var(--sidebar-brand);
}

/* Primary nav */
.rail__nav {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-left: 8px;
}
.navitem {
  position: relative;
  display: flex;
  align-items: center;
  gap: 11px;
  height: 38px;
  padding: 0 12px;
  border-radius: var(--radius-md);
  color: var(--sidebar-nav-idle);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.1px;
  transition: background var(--motion-fast), color var(--motion-fast);
}
.navitem:hover {
  background: var(--sidebar-nav-hover-bg);
}
.navitem--active {
  background: var(--sidebar-nav-active-bg);
  color: var(--sidebar-nav-active-text);
  font-weight: 600;
}
.navitem__icon {
  flex-shrink: 0;
}
.navitem__bar {
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 18px;
  border-radius: 3px;
  background: var(--sidebar-accent-bar);
}

/* My classes */
.myclasses {
  margin-top: 24px;
  padding-left: 8px;
}
.myclasses__head {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 0 4px 10px;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.myclasses__chev {
  color: var(--sidebar-eyebrow);
  transition: transform var(--motion-fast);
}
.myclasses__chev--collapsed {
  transform: rotate(-90deg);
}
.myclasses__eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--sidebar-eyebrow);
}
.myclasses__count {
  margin-left: auto;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  color: var(--sidebar-eyebrow);
}
.myclasses__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.classrow {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  height: 40px;
  flex-shrink: 0;
  padding: 0 10px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  cursor: pointer;
  transition: background var(--motion-fast);
}
.classrow:hover {
  background: var(--sidebar-nav-hover-bg);
}
.classrow--active {
  background: var(--sidebar-nav-active-bg);
}
.classrow__dot {
  width: 9px;
  height: 9px;
  border-radius: 3px;
  flex-shrink: 0;
}
.classrow__name {
  flex: 1;
  min-width: 0;
  text-align: left;
  font-size: 13.5px;
  font-weight: 500;
  letter-spacing: -0.1px;
  color: var(--sidebar-nav-idle);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.classrow--active .classrow__name {
  font-weight: 600;
  color: var(--sidebar-nav-active-text);
}
.classrow__count {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  color: var(--sidebar-eyebrow);
}

/* Spacer + account */
.rail__spacer {
  flex: 1;
  min-height: 18px;
}
.account {
  position: relative;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid var(--sidebar-border);
}
.account__row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.account__avatar {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
}
.account__id {
  flex: 1;
  min-width: 0;
}
.account__name {
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.1px;
  color: var(--sidebar-foot-name);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.account__meta {
  font-size: 11.5px;
  color: var(--sidebar-foot-meta);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.account__gear {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--sidebar-foot-meta);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background var(--motion-fast);
}
.account__gear:hover {
  background: var(--sidebar-nav-hover-bg);
}
.account__menu {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: var(--z-dropdown);
}

@media (prefers-reduced-motion: reduce) {
  .navitem,
  .classrow,
  .myclasses__chev,
  .account__gear {
    transition: none;
  }
}
</style>
